import React from "react";
import { Job } from "../../../company/JobManagement/model/JobModel";
import { getJobTitleInitials } from "../../../../utils/jobTitleDefault"
import { formatDate } from '../../../../utils/date';

// Define enum for view modes
export enum ViewMode {
  GRID = 'grid',
  LIST = 'list'
}

interface JobCardProps {
  job: Job;
  onViewDetails: (job: Job) => void;
  viewMode?: ViewMode | 'grid' | 'list';
  isLoading?: boolean;
}

const JobCard: React.FC<JobCardProps> = ({ job, onViewDetails, viewMode = ViewMode.GRID, isLoading = false }) => {
  const skills = (job.skills) ?? [];
  const jobInitials = getJobTitleInitials(job.company.name);

  // Helper function to normalize viewMode
  const getNormalizedViewMode = (mode: ViewMode | 'grid' | 'list'): ViewMode => {
    if (typeof mode === 'string') {
      return mode === 'list' ? ViewMode.LIST : ViewMode.GRID;
    }
    return mode;
  };

  const normalizedViewMode = getNormalizedViewMode(viewMode);

  return (
    <div className="bg-white rounded-md border shadow-sm hover:shadow-md transition-shadow duration-200 p-3">
      {/* Top section with avatar and title */}
      <div className="flex items-start justify-between gap-6 pb-3">
        <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
          <span className="text-indigo-600 font-medium text-sm">{jobInitials}</span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-medium text-gray-900 truncate">{job.title}</h2>
              <p className="text-xs text-gray-500 truncate">
                {job.company?.name || 'Company'}
              </p>
            </div>

            {/* Salary and Posted Date - Only for list view, positioned top-right */}
            {normalizedViewMode === ViewMode.LIST && (
              <div className="flex flex-col items-end ml-4">
                {/* Salary */}
                <div className="mb-2">
                  <span className="text-xs font-medium text-gray-700">
                    {job.salary_currency} {job.salary_min} - {job.salary_max}
                  </span>
                </div>

                <div className="text-gray-500 text-xs">
                  Posted:{" "}
                  <span className="font-medium">
                    {job.published_at
                      ? formatDate(job.published_at)
                      : "Recently"}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Job Type Badges */}
      <div className="mb-3 flex flex-wrap gap-1">
        {job.job_type && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
            {job.job_type}
          </span>
        )}
        {job.work_mode && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
            {job.work_mode}
          </span>
        )}
        {job.experience_level && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
            {job.experience_level}
          </span>
        )}
      </div>

      {/* Location display - Different layout for list vs grid */}
      {job.location && (
        <>
          {/* List view: Location and Button on same line */}
          {normalizedViewMode === ViewMode.LIST ? (
            <div className="flex items-center justify-between ">
              <div className="text-xs text-gray-500 mt-1">
                <span>{job.location}</span>
              </div>

              {/* View Details Button - Only for list view, on same line as location */}
              <div className="flex space-x-2">
                <button
                  onClick={() => onViewDetails(job)}
                  className="flex-1 text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                >
                  View
                </button>

              </div>
            </div>
          ) : (
            /* Grid view: Location only */
            <div className="text-xs text-gray-500 mt-1">
              <span>{job.location}</span>
            </div>
          )}
        </>
      )}

      {/* Salary Section - Only show in grid view */}
      {normalizedViewMode === ViewMode.GRID && (
        <div className="mb-2">
          <span className="text-xs font-medium text-gray-700">
            {job.salary_currency} {job.salary_min} - {job.salary_max}
          </span>
        </div>
      )}

      {/* Skills */}
      {skills.length > 0 && (
        <div className="mb-4">
          <div className="text-gray-700 text-sm font-semibold mb-2">Skills:</div>
          <div className="flex flex-wrap gap-1.5">
            {skills.slice(0, 3).map((skill: any, index: number) => (
              <span
                key={skill.id ?? index}
                className={`px-2 py-1 rounded text-xs font-medium ${skill.is_required
                  ? "bg-red-50 text-red-700"
                  : "bg-gray-100 text-gray-700"
                  }`}
              >
                {skill.name ?? skill.toString()}
              </span>
            ))}
            {skills.length > 3 && (
              <span className="text-gray-500 text-xs px-2 py-1">
                +{skills.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Footer - Only for grid view to show posted date and view details button */}
      {normalizedViewMode === ViewMode.GRID && (
        <div className="mt-3">
          {/* Posted Date */}
          <div className="flex space-x-2">
            <div className="flex-1 text-gray-500 text-xs">
              Posted:{" "}
              <span className="font-medium">
                {job.published_at
                  ? formatDate(job.published_at)
                  : "Recently"}
              </span>
            </div>
            <button
              onClick={() => onViewDetails(job)}
              className="flex-1 text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
            >
              View
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobCard;